import { CountryDefaultCurrencyRole, ICountryDetail, IUpdateCountry } from 'types/countries';
import { parseExchangeRateValue } from '../currenciesTab/components/currencyTableRow/currencyTableRow.utils';

const ALLOWED_DOCUMENT_EXAMPLE_CHARS = new Set(['X', '0', 'Y', '.', '-', '/']);

/**
 * Obtiene la extensión en minúsculas de un nombre de archivo (incluye el punto).
 */
export const getFileExtension = (fileName: string): string =>
  fileName.substring(fileName.lastIndexOf('.')).toLowerCase();

/**
 * Props de formatter y parser para el input de impuestos (porcentaje).
 */
export const countryTaxRateInputNumberProps = {
  formatter: (value: string | number | undefined) => `${value}%`,
  parser: (value: string | undefined) => value?.replace('%', '') ?? ''
};

/**
 * Props de formatter y parser para el input de indicativo telefónico.
 */
export const countryPhoneCodeInputNumberProps = {
  formatter: (value: string | number | undefined) => `+ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
  parser: (value: string | undefined) => value?.replace(/\+\s?|(,*)/g, '') ?? ''
};

/**
 * Convierte la tasa de cambio del catálogo a número para mostrarla en solo lectura.
 */
export const parseCatalogExchangeRate = (exchangeRateValue?: string | number | null): number | undefined => {
  const parsedExchangeRate = parseExchangeRateValue(exchangeRateValue ?? undefined);
  return parsedExchangeRate === null ? undefined : parsedExchangeRate;
};

/**
 * Normaliza el ejemplo de documento fiscal permitiendo solo caracteres válidos.
 */
export const normalizeDocumentExample = (example: string): string => {
  if (!example?.length) {
    return '';
  }

  const upperCaseExample = example.toLocaleUpperCase();

  return upperCaseExample
    .split('')
    .filter(letter => ALLOWED_DOCUMENT_EXAMPLE_CHARS.has(letter))
    .join('');
};

/**
 * Construye el estado inicial editable de una fila de país desde el detalle recibido.
 */
export const buildCountryRowFormState = (countryData: ICountryDetail): IUpdateCountry => ({
  taxRate: countryData.taxRate,
  phoneCode: countryData.phoneCode,
  documentExample: countryData.documentExample,
  defaultCurrencyRole: countryData.defaultCurrencyRole ?? CountryDefaultCurrencyRole.PRIMARY,
  registry: !!countryData.registry,
  buy: !!countryData.buy,
  logisticService: !!countryData.logisticService,
  storage: !!countryData.storage
});

/**
 * Indica si los campos obligatorios de la fila de país no cumplen las validaciones mínimas.
 */
export const isCountryRowFormInvalid = (
  formData: IUpdateCountry | undefined,
  exchangeRate: number | undefined
): boolean => {
  if (!formData) return true;

  const { taxRate, phoneCode, documentExample } = formData;
  const taxRateIsNotValid = !['number', 'string'].includes(typeof taxRate);
  const hasExchangeRate = exchangeRate != null;

  return !hasExchangeRate || taxRateIsNotValid || !phoneCode || !documentExample;
};

/**
 * Estado de presencia de valor para estilos filled en los campos de la fila.
 */
export interface ICountryRowFieldHasValue {
  exchangeRate: boolean;
  taxes: boolean;
  phoneCode: boolean;
  document: boolean;
  provider: boolean;
}

/**
 * Calcula qué campos de la fila tienen valor para aplicar clases de estilo filled.
 */
export const buildCountryRowFieldHasValue = (
  formData: IUpdateCountry | undefined,
  exchangeRate: number | undefined,
  providerCount: number
): ICountryRowFieldHasValue => ({
  exchangeRate: exchangeRate != null,
  taxes: !!formData?.taxRate,
  phoneCode: !!formData?.phoneCode,
  document: !!formData?.documentExample,
  provider: providerCount > 0
});

/**
 * Concatena clases base con filledLabel o filledInput según si el campo tiene valor.
 */
export const getCountryRowFilledClassName = (
  baseClass: string,
  hasValue: boolean,
  filledModifier: 'filledLabel' | 'filledInput' = 'filledLabel'
): string => (hasValue ? `${baseClass} ${filledModifier}` : baseClass);
